'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { cn } from '@/shared/lib/cn';
import { FOOTER_REVEAL_TRANSFORM } from '@/shared/lib/bottom-bar-reveal';
import { NestedSheetStackProvider } from '@/shared/ui';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import type { AlbumLanguage } from '@/features/design/album';
import type { AlbumCostPredictionParams } from '../api/album-service';
import { useAlbumCostPrediction } from '../hooks/use-album-cost-prediction';
import { useAlbumPanel, type AlbumPanelAngle } from '../hooks/useAlbumPanel';
import { AlbumPanelSettings } from './AlbumPanelSettings';
import { AlbumsList } from './AlbumsList';
import { AlbumProgressCard } from './AlbumProgressCard';

interface AlbumCollapsiblePanelProps {
  conceptId: string;
  conceptAngles: AlbumPanelAngle[];
  isArchived?: boolean;
  projectId?: string;
  conceptTitle?: string | null;
  conceptNumber?: number;
  coverImageUrl?: string | null;
  /** Tailwind `bottom-*` class for the floating Generate capsule — clears the
   *  bottom-nav island (single-column / stacked album tab). */
  footerBottomClassName?: string;
}

export function AlbumCollapsiblePanel({
  conceptId,
  conceptAngles,
  isArchived = false,
  projectId,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
  footerBottomClassName = 'bottom-6',
}: AlbumCollapsiblePanelProps) {
  const t = useTranslations('album');
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  // After the user confirms generation, scroll the panel into view so the new
  // in-progress card (which appears at the top of the list) is not left
  // off-screen above the generate button on single-column phone layouts.
  const scrollPanelIntoView = () => {
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const [duplicateInfo, setDuplicateInfo] = useState<{
    generatedAt?: string;
  } | null>(null);

  const {
    // State
    highlightedAlbumId,
    selectedLanguage,
    setSelectedLanguage,

    // Plan features
    hasAdvancedAlbumOptions,
    isAlbumTemplatesAvailable,

    // Album settings
    selectedTemplate,
    storedSectionToggles,
    storedSelectedLanguage,
    storedRequirements,
    storedIsExtendedThinking,
    storedMeasurementSystem,
    updateSetting,

    // Album operations
    albums,
    mergedAlbums,
    generateAlbum,
    lastGenerateResult,
    resetGenerateResult,
    deleteAlbum,
    downloadAlbum,
    isGenerating,
    isDeleting,
    hasAlbumInQueue,
    albumInQueue,

    // Selected renders
    selectedRenders,
    selectedRenderUrls,

    // Cost-estimate picker (FE4)
    estimatePicker,
  } = useAlbumPanel({
    conceptId,
    conceptAngles,
    projectId,
  });

  const renderedCount = Object.keys(selectedRenders).length;
  const totalCount = conceptAngles.length;
  const hasPartialRenders = renderedCount > 0 && renderedCount < totalCount;

  // Server-predicted token cost for the CURRENT settings — shown as a suffix
  // on the Generate button. The cost-relevant params mirror the generate
  // request below 1:1 (memoized so the hook's debounce is stable).
  const costPredictionParams = useMemo<AlbumCostPredictionParams>(
    () => ({
      renders_count: renderedCount,
      include_materials: storedSectionToggles.includeMaterials,
      include_blueprints: storedSectionToggles.includeBlueprints,
      include_preparation_guide: storedSectionToggles.includePreparationGuide,
      include_safety_requirements:
        storedSectionToggles.includeSafetyRequirements,
      include_building_regulations:
        storedSectionToggles.includeBuildingRegulations,
      is_extended_thinking: storedIsExtendedThinking,
      // True only for the "New estimate" picker option — exactly when the
      // generate request spreads `generate_new_estimate: true` (see
      // useAlbumEstimatePicker.estimatePayload).
      generate_new_estimate:
        'generate_new_estimate' in estimatePicker.estimatePayload,
    }),
    [
      renderedCount,
      storedSectionToggles.includeMaterials,
      storedSectionToggles.includeBlueprints,
      storedSectionToggles.includePreparationGuide,
      storedSectionToggles.includeSafetyRequirements,
      storedSectionToggles.includeBuildingRegulations,
      storedIsExtendedThinking,
      estimatePicker.estimatePayload,
    ]
  );
  const {
    tokens: predictedTokens,
    isSignificantCost,
    warningMessage: costWarningMessage,
  } = useAlbumCostPrediction(conceptId, costPredictionParams);

  const handleGenerateAlbum = (forceRegenerate = false) => {
    // Extract render IDs from selectedRenders object. No empty-renders guard:
    // the request fires and the server's localized `main_render_required`
    // message surfaces as an error toast (use-album onError; API-driven
    // messages — same contract as the estimate CTA).
    const selectedRenderIds = Object.values(selectedRenders);

    // Apple parity (AlbumGeneratorView): no client-side plan/balance preflight
    // for album generation. The server enforces limits and returns a 422 with
    // an inline paywall payload; `use-album.ts` mutation onError routes that
    // to the shared `LimitPaywallDialog` via `useLimitErrorPaywall`.
    generateAlbum({
      // Template is the design axis. Send the actually selected template —
      // Designer holders can pick architect/studio; the server falls back to
      // standard for any not-yet-built design. Blueprints are a separate free
      // toggle that replaces the old full/short split.
      template: selectedTemplate,
      // Content-section flags — always sent (all default ON) so the server
      // records the snapshot explicitly. `include_blueprints` gates ONLY the
      // facade projections (blueprint drawings).
      include_blueprints: storedSectionToggles.includeBlueprints,
      include_materials: storedSectionToggles.includeMaterials,
      include_preparation_guide: storedSectionToggles.includePreparationGuide,
      include_safety_requirements:
        storedSectionToggles.includeSafetyRequirements,
      include_building_regulations:
        storedSectionToggles.includeBuildingRegulations,
      language: selectedLanguage || storedSelectedLanguage,
      // Measurement system: sent only when the user explicitly picked Metric
      // or Imperial — "Auto" omits the field so the server decides.
      ...(storedMeasurementSystem !== 'auto'
        ? { measurement_system: storedMeasurementSystem }
        : {}),
      selected_renders: selectedRenderIds,
      // Always sent (default false) so the server records the snapshot
      // explicitly; Pro+ gating is enforced server-side.
      is_extended_thinking: storedIsExtendedThinking,
      // Estimate choice: explicit estimate_id (may be a sibling design's) or
      // generate_new_estimate — see useAlbumEstimatePicker.estimatePayload.
      ...estimatePicker.estimatePayload,
      ...(storedRequirements ? { requirements: storedRequirements } : {}),
      ...(forceRegenerate ? { force_regenerate: true } : {}),
    });
  };

  // Detect duplicate_exists response
  useEffect(() => {
    if (lastGenerateResult?.duplicate_exists && !showDuplicateDialog) {
      setDuplicateInfo({
        generatedAt: lastGenerateResult.generated_at,
      });
      setShowDuplicateDialog(true);
    }
  }, [lastGenerateResult]);

  const handleDownloadAlbum = (albumId?: string) => {
    downloadAlbum(albumId);
  };

  return (
    <div ref={panelRef}>
      {/* Album generation progress card */}
      {hasAlbumInQueue && albumInQueue && (
        <div className="pb-4">
          <AlbumProgressCard album={albumInQueue} projectId={projectId} />
        </div>
      )}

      {/* Albums list */}
      <AlbumsList
        albums={mergedAlbums}
        highlightedAlbumId={highlightedAlbumId}
        isDeleting={isDeleting}
        onDelete={deleteAlbum}
        onDownload={handleDownloadAlbum}
        conceptTitle={conceptTitle}
        conceptNumber={conceptNumber}
        coverImageUrl={coverImageUrl}
      />

      {/* Inline album settings (no drawer) — template / language / blueprints
          / requirements / extended thinking. The Generate confirmation floats
          as a capsule above the bottom nav (below), mirroring the design tab.
          `isStacked`: this panel renders only on the single-column concept page
          (the two-column layout uses AlbumListWithViewer), so its sub-panels
          must use the stacked fixed-overlay path — same as the design tab — and
          drive the bottom-nav hide while open (NestedSheetStackProvider). */}
      <NestedSheetStackProvider isStacked>
        <AlbumPanelSettings
          requirements={storedRequirements}
          onRequirementsChange={(value) => updateSetting('requirements', value)}
          isArchived={isArchived}
          selectedTemplate={selectedTemplate}
          onTemplateChange={(template) =>
            updateSetting('selectedTemplate', template)
          }
          sectionToggles={storedSectionToggles}
          onSectionToggleChange={(key, checked) =>
            updateSetting(key, checked)
          }
          selectedLanguage={selectedLanguage || storedSelectedLanguage}
          onLanguageChange={(lang) => {
            setSelectedLanguage(lang);
            updateSetting('selectedLanguage', lang as AlbumLanguage);
          }}
          hasAdvancedAlbumOptions={hasAdvancedAlbumOptions}
          isAlbumTemplatesAvailable={isAlbumTemplatesAvailable}
          isExtendedThinking={storedIsExtendedThinking}
          onIsExtendedThinkingChange={(checked) =>
            updateSetting('isExtendedThinking', checked)
          }
          measurementSystem={storedMeasurementSystem}
          onMeasurementSystemChange={(system) =>
            updateSetting('measurementSystem', system)
          }
          hasPartialRenders={hasPartialRenders}
          renderedCount={renderedCount}
          totalCount={totalCount}
          selectedRenderUrls={selectedRenderUrls}
          estimatePicker={estimatePicker}
          isGenerating={isGenerating ?? false}
          // Server-localized significant-cost warning — null while an album is
          // queued so the banner never shows alongside the generating state.
          costWarningMessage={hasAlbumInQueue ? null : costWarningMessage}
        />
      </NestedSheetStackProvider>

      {/* Duplicate album confirmation dialog */}
      <AlertDialog
        open={showDuplicateDialog}
        onOpenChange={setShowDuplicateDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('duplicateExists.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {duplicateInfo?.generatedAt
                ? t('duplicateExists.message', {
                    date: new Date(
                      duplicateInfo.generatedAt
                    ).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  })
                : t('duplicateExists.messageNoDate')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowDuplicateDialog(false);
                setDuplicateInfo(null);
                resetGenerateResult();
              }}
            >
              {t('duplicateExists.useExisting')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowDuplicateDialog(false);
                setDuplicateInfo(null);
                resetGenerateResult();
                handleGenerateAlbum(true);
              }}
            >
              {t('duplicateExists.generateNew')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating Generate capsule — above the bottom nav island, shaped like
          it, always visible over the scroll (Apple/Android parity with the
          design tab). Generation gating is server-driven (422 → paywall). */}
      {!isArchived && (
        <div
          className={cn(
            'fixed inset-x-0 z-40 flex justify-center px-4 pointer-events-none',
            footerBottomClassName
          )}
          // Ride the scroll-linked nav reveal (mirrors the Design tab footer).
          style={{
            transform: FOOTER_REVEAL_TRANSFORM,
            // Center over the content column, not the viewport (offset past the
            // left sidebar when expanded — see DesktopShell `--content-inset-left`).
            paddingLeft: 'calc(1rem + var(--content-inset-left, 0px))',
          }}
        >
          <Button
            onClick={() => {
              handleGenerateAlbum();
              scrollPanelIntoView();
            }}
            // Stays ACTIVE with no completed renders — see handleGenerateAlbum.
            disabled={isGenerating || hasAlbumInQueue}
            variant={albums.length > 0 ? 'secondary' : 'default'}
            size="cta"
            className="pointer-events-auto shadow-lg shadow-black/20"
          >
            {isGenerating || hasAlbumInQueue ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('generatingAlbum')}
              </>
            ) : (
              <>
                {albums.length > 0
                  ? t('generateNewVersion')
                  : t('generateAlbum')}
                {/* Server-predicted token cost — suffix only when the server
                    flags the cost as significant (threshold lives server-side;
                    loading/error/below-threshold → plain label, no price). */}
                {predictedTokens !== undefined && isSignificantCost && (
                  <span className="inline-flex items-center gap-1">
                    · ≈{predictedTokens}
                    <WalnutIcon className="h-3.5 w-[11px] shrink-0" />
                  </span>
                )}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
